if (!customElements.get('resident-wardrobe')) {
  class ResidentWardrobe extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-wardrobe-config]').textContent);
      this.section = this.closest('.product-section') || this.closest('[id^="shopify-section-"]');
      this.section?.classList.add('pkd-has-builder');
      document.body.classList.add('pkd-resident-product');
      this.root = this.config.cartRoot || window.Shopify?.routes?.root || '/';
      if (!this.root.endsWith('/')) this.root += '/';
      this.keyName = `pipkind-wardrobe:${this.dataset.productId}`;
      try {
        this.group = sessionStorage.getItem(this.keyName) || `pk-${this.dataset.productId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
        sessionStorage.setItem(this.keyName, this.group);
      } catch (_) {
        this.group = `pk-${this.dataset.productId}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      }
      this.mode = this.dataset.dressedAvailable === 'true' ? 'dressed' : 'custom';
      this.extraOpen = this.mode === 'custom';
      this.cartIntent = false;
      this.touched = false;
      this.syncing = false;
      this.dirty = false;
      this.cartReady = false;
      this.failed = false;
      this.revision = 0;
      this.sectionsRevision = 0;
      this.cartProperties = {
        _pipkind_wardrobe: this.group
      };
      this.custom = this.querySelector('[data-wardrobe-custom]');
      this.extrasToggle = this.querySelector('[data-wardrobe-extras]');
      this.status = this.querySelector('[data-wardrobe-status]');
      this.selectionHint = document.createElement('p');
      this.selectionHint.className = 'resident-wardrobe__selection-hint';
      this.selectionHint.textContent = 'Try a new look. Your selections are added only when you choose Add to cart or Checkout.';
      this.custom.querySelector('.resident-wardrobe__subheading').after(this.selectionHint);
      // Retain the theme's calculation nodes without another visible purchase section.
      this.selectionState = document.createElement('div');
      this.selectionState.className = 'resident-wardrobe__selection-state';
      this.selectionState.hidden = true;
      this.selectionState.append(this.querySelector('[data-wardrobe-base-card]'), this.querySelector('[data-wardrobe-preview]'), this.querySelector('.resident-wardrobe__summary'));
      this.custom.after(this.selectionState);
      this.products = [...this.querySelectorAll('[data-wardrobe-product]')];
      this.abort = new AbortController();
      const listen = (node, type, handler, capture = false) => node?.addEventListener(type, handler, {
        capture,
        signal: this.abort.signal
      });
      this.querySelectorAll('[data-wardrobe-mode]').forEach(button => listen(button, 'click', () => this.changeMode(button.dataset.wardrobeMode)));
      listen(this.extrasToggle, 'click', () => {
        this.extraOpen = !this.extraOpen;
        this.updateExtrasUI();
      });
      this.products.forEach(row => {
        // Cart edits restore here so choosing another accessory preserves prior quantities.
        row.quantityInput = document.createElement('input');
        row.quantityInput.type = 'hidden';
        row.quantityInput.value = '1';
        row.quantityInput.dataset.wardrobeQuantity = '';
        row.append(row.quantityInput);
        listen(row.querySelector('[data-wardrobe-check]'), 'change', () => this.changed());
        listen(row.querySelector('[data-wardrobe-variant]'), 'change', () => this.changed());
      });
      this.form = this.section?.querySelector('product-form form');
      this.formVariant = this.form?.querySelector('input[name="id"]');
      this.quantityInput = this.form?.querySelector('input[name="quantity"]');
      const legacy = this.querySelector('[data-wardrobe-checkout]');
      this.checkout = this.form?.querySelector('button[name="add"]') || legacy;
      this.checkoutPrice = this.checkout?.querySelector('.product-sub-total, [data-wardrobe-checkout-price]');
      this.checkoutLabel = this.checkout?.querySelector('.add-to-cart-label');
      if (this.form && this.checkout !== legacy) {
        legacy?.remove();
        this.checkout.dataset.wardrobeCheckout = '';
        this.checkout.setAttribute('aria-haspopup', 'dialog');
        const wrapper = this.form.querySelector('.product-form__buttons-wrapper');
        this.directCheckout = document.createElement('button');
        this.directCheckout.type = 'button';
        this.directCheckout.className = 'resident-wardrobe__direct-checkout button button--secondary';
        this.directCheckout.dataset.wardrobeDirectCheckout = '';
        this.directCheckout.textContent = 'Checkout';
        wrapper.append(this.directCheckout);
        wrapper.after(this.status);
        if (this.quantityInput) {
          const label = document.createElement('label');
          label.className = 'resident-wardrobe__doll-quantity-label';
          label.htmlFor = this.quantityInput.id;
          label.textContent = 'Doll quantity';
          this.quantityInput.closest('quantity-input').before(label);
        }
        listen(this.form, 'submit', event => {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (this.form.reportValidity()) this.requestCartOpen(this.checkout);
        }, true);
        listen(this.directCheckout, 'click', () => {
          if (this.form.reportValidity()) this.requestCartOpen(this.directCheckout, true);
        });
      } else {
        this.append(this.status, this.checkout);
        listen(this.checkout, 'click', () => this.requestCartOpen(this.checkout));
      }
      listen(this.quantityInput, 'input', () => {
        if (this.quantityInput.validity.valid) this.quantityChanged();
      });
      listen(this.quantityInput, 'change', () => {
        this.normalizeQuantity(this.quantityInput);
        this.quantityChanged();
      });
      // Capture the shopper's cart intent before the drawer's debounced request.
      // A quick return to accessories must not overwrite that quantity.
      listen(document, 'change', event => {
        const input = event.target;
        if (!input.matches?.('cart-drawer input[name="updates[]"]')) return;
        const line = this.lastConfirmedCart?.items[Number(input.dataset.index) - 1];
        const quantity = Number(input.value);
        if (line?.properties?._pipkind_wardrobe !== this.group || !Number.isInteger(quantity) || quantity < 1) return;
        if ([this.dataset.dressedId, this.dataset.undressedId].includes(String(line.variant_id))) {
          if (this.quantityInput) this.quantityInput.value = String(quantity);
        } else {
          this.products.forEach(row => {
            const field = row.querySelector('[data-wardrobe-variant]');
            if (field.value === String(line.variant_id)) row.quantityInput.value = String(quantity);
          });
        }
        this.revision += 1;
        this.cartReady = false;
        this.renderPreview();
        this.updatePurchaseUI();
        this.dispatchSelection();
      }, true);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => {
          if (event.source !== 'resident-wardrobe' && !this.dirty && !this.syncing) this.restoreFromCart(true);
        });
      }
      listen(window, 'pageshow', event => {
        if (event.persisted) this.restoreFromCart(true);
      });
      listen(document, 'visibilitychange', () => {
        if (!document.hidden && !this.dirty && !this.syncing) this.restoreFromCart(true);
      });
      this.updateModeUI();
      this.renderPreview();
      this.switchMedia(this.mode);
      this.restoreFromCart().finally(() => window.PipkindBfcm?.restoreSelection(this));
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      this.abort?.abort();
      this.unsubscribe?.();
      if (!document.querySelector('resident-wardrobe')) document.body.classList.remove('pkd-resident-product');
    }
    normalizeQuantity(input) {
      if (input) input.value = String(Math.max(1, Math.floor(Number(input.value)) || 1));
    }
    dollQuantity() {
      return Math.max(1, Math.floor(Number(this.quantityInput?.value)) || 1);
    }
    accessoryQuantity(row) {
      return Math.max(1, Math.floor(Number(row.quantityInput?.value)) || 1);
    }
    selectedCount() {
      return this.products.filter(row => row.querySelector('[data-wardrobe-check]')?.checked).length;
    }
    builderActive() {
      return true; // This template commits the complete selection only on a purchase action.
    }
    baseAvailable() {
      return this.dataset[this.mode === 'custom' ? 'undressedAvailable' : 'dressedAvailable'] === 'true';
    }
    changeMode(mode) {
      if (!['dressed', 'custom'].includes(mode) || mode === this.mode) return;
      if (this.dataset[mode === 'custom' ? 'undressedAvailable' : 'dressedAvailable'] !== 'true') return;
      this.mode = mode;
      this.extraOpen = this.mode === 'custom';
      this.switchMedia(mode);
      this.changed();
    }
    quantityChanged() {
      if (this.dollQuantity() === this.lastDollQuantity) return;
      this.touched = true;
      this.revision += 1;
      this.renderPreview();
      this.selectionDirty = true;
      this.cartReady = false;
      this.failed = false;
      this.updatePurchaseUI();
      this.dispatchSelection();
    }
    changed() {
      this.touched = true;
      this.revision += 1;
      this.selectionDirty = true;
      this.cartReady = false;
      this.failed = false;
      this.updateModeUI();
      this.renderPreview();
      this.updatePurchaseUI();
      this.dispatchSelection();
    }
    dispatchSelection() {
      window.dispatchEvent(new CustomEvent('resident-wardrobe:selection', {
        detail: {
          active: this.builderActive(),
          mode: this.mode
        }
      }));
    }
    updateExtrasUI() {
      this.custom.hidden = !this.extraOpen;
      this.extrasToggle?.setAttribute('aria-expanded', String(this.extraOpen));
      const extrasLabel = this.querySelector('[data-wardrobe-extras-label]');
      if (extrasLabel) extrasLabel.textContent = this.extraOpen ? 'Close wardrobe' : 'Make it their own';
      const hint = this.querySelector('[data-bfcm-extras-hint]');
      if (hint) hint.textContent = this.selectedCount() ? `${this.selectedCount()} selected · ${this.format(this.accessoryTotal())}` : 'Add outfits & accessories';
      if (this.extraOpen) this.extrasToggle?.classList.remove('pkd-bfcm-attention');
    }
    updateModeUI() {
      const active = this.builderActive();
      this.updateExtrasUI();
      this.custom.querySelector('.resident-wardrobe__subheading').textContent = this.mode === 'custom' ? 'Add outfits & accessories (optional)' : 'Add extra outfits & accessories';
      this.querySelectorAll('[data-wardrobe-mode]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.wardrobeMode === this.mode)));
      this.section?.classList.toggle('pkd-dress-mode', active);
      this.section?.querySelectorAll('.product-block-buy_buttons__wrapper').forEach(element => {
        element.style.display = '';
      });
      if (this.formVariant) this.formVariant.value = this.mode === 'custom' ? this.dataset.undressedId : this.dataset.dressedId;
      const baseImage = this.querySelector('[data-wardrobe-base-image]');
      baseImage.src = this.mode === 'custom' ? this.dataset.undressedCardImage : this.dataset.dressedCardImage;
      baseImage.alt = `${this.config.baseTitle} — ${this.mode === 'custom' ? 'undressed' : 'dressed'} base doll`;
      this.querySelector('[data-wardrobe-base-name]').textContent = `${this.mode === 'custom' ? 'Doll only' : 'Dressed'} ${this.config.baseTitle}`;
      const price = Number(this.mode === 'custom' ? this.dataset.undressedPrice : this.dataset.dressedPrice);
      const compare = this.mode === 'custom' ? Number(this.dataset.undressedCompare || 0) : Number(this.dataset.dressedCompare);
      this.querySelector('[data-wardrobe-base-price]').textContent = this.format(price);
      this.section?.querySelectorAll('.product__price .price__regular .price-item--regular, .product__price .price-item--sale').forEach(node => {
        node.textContent = this.format(price);
      });
      this.section?.querySelectorAll('.product__price .price__sale .price-item--regular').forEach(node => {
        node.textContent = this.format(compare || price);
      });
      this.section?.querySelectorAll('.product__price .price').forEach(node => node.classList.toggle('price--on-sale', compare > price));
      this.section?.querySelectorAll('.product__price .badge__on-sale').forEach(node => {
        node.hidden = !(compare > price);
        if (compare > price) node.textContent = `SAVE ${Math.round((compare - price) * 100 / compare)}%`;
      });
      this.section?.querySelectorAll('.btn-modal-notify-stock').forEach(node => node.classList.toggle('hidden', this.baseAvailable()));
      this.checkout?.classList.remove('hidden');
      this.checkout?.querySelector('.sold-out-label')?.classList.add('hidden');
      this.checkoutLabel?.classList.remove('hidden');
      this.updatePurchaseUI();
      this.dispatchSelection();
    }
    updatePurchaseUI() {
      const pending = this.dirty || this.syncing;
      const label = !this.baseAvailable() ? 'Sold out' : this.failed ? 'Retry update' : pending ? 'Updating…' : this.cartReady ? 'View cart' : 'Add to cart';
      if (this.checkoutLabel) this.checkoutLabel.textContent = label;else if (this.checkout) this.checkout.firstChild.textContent = `${label} · `;
      if (this.checkout) {
        this.checkout.hidden = false;
        this.checkout.disabled = !this.baseAvailable();
        this.checkout.setAttribute('aria-label', `${label}, ${this.dollQuantity()} ${this.dollQuantity() === 1 ? 'doll' : 'dolls'}${this.selectedCount() ? ' with accessories' : ''}`);
        this.checkout.setAttribute('aria-busy', String(pending));
      }
      this.form?.querySelectorAll('.shopify-payment-button').forEach(node => {
        node.hidden = this.builderActive();
      });
      if (this.directCheckout) {
        this.directCheckout.hidden = !this.builderActive();
        this.directCheckout.disabled = !this.baseAvailable() || this.failed;
        this.directCheckout.textContent = pending ? 'Preparing checkout…' : 'Checkout';
        this.directCheckout.setAttribute('aria-busy', String(pending));
      }
    }
    switchMedia(mode) {
      const mediaId = mode === 'custom' ? this.dataset.undressedMediaId : this.dataset.dressedMediaId;
      const gallery = this.section?.querySelector('media-gallery');
      if (mediaId && typeof gallery?.setActiveMedia === 'function') gallery.setActiveMedia(`${this.dataset.sectionId}-${mediaId}`, false);
    }
    selectedItems() {
      if (!this.builderActive()) return [];
      const baseId = this.mode === 'custom' ? this.dataset.undressedId : this.dataset.dressedId;
      const items = [{
        id: Number(baseId),
        quantity: this.dollQuantity(),
        properties: this.cartProperties
      }];
      this.products.forEach(row => {
        if (!row.querySelector('[data-wardrobe-check]').checked) return;
        const field = row.querySelector('[data-wardrobe-variant]');
        if (field.value) items.push({
          id: Number(field.value),
          quantity: this.accessoryQuantity(row),
          properties: this.cartProperties
        });
      });
      const unique = new Map();
      items.forEach(item => {
        if (unique.has(String(item.id))) unique.get(String(item.id)).quantity += item.quantity;else unique.set(String(item.id), item);
      });
      return [...unique.values()];
    }
    format(cents) {
      if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, this.config.moneyFormat);
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
    }
    renderPreview() {
      const preview = this.querySelector('[data-wardrobe-preview]');
      preview.replaceChildren();
      this.lastDollQuantity = this.dollQuantity();
      let cents = Number(this.mode === 'custom' ? this.dataset.undressedPrice : this.dataset.dressedPrice) * this.dollQuantity();
      let units = 0;
      this.products.forEach(row => {
        const checked = row.querySelector('[data-wardrobe-check]').checked;
        row.quantityInput.disabled = !checked;
        const field = row.querySelector('[data-wardrobe-variant]');
        const option = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
        const price = Number(option?.dataset.price || 0);
        row.querySelector('[data-wardrobe-price]').textContent = this.format(price);
        if (!checked) return;
        const quantity = this.accessoryQuantity(row);
        units += quantity;
        cents += price * quantity;
        const item = document.createElement('li');
        item.textContent = `${row.dataset.title} × ${quantity} · ${this.format(price * quantity)}`;
        preview.append(item);
      });
      this.estimatedTotal = cents;
      this.displayTotal = cents;
      this.querySelector('[data-wardrobe-count]').textContent = 'Selection total';
      this.querySelector('[data-wardrobe-base-name]').textContent = `${this.config.baseTitle} × ${this.dollQuantity()}`;
      this.querySelector('.resident-wardrobe__base-badge').textContent = this.mode === 'custom' ? 'Doll only' : 'Dressed as pictured';
      this.querySelector('[data-wardrobe-base-price]').textContent = this.format(Number(this.mode === 'custom' ? this.dataset.undressedPrice : this.dataset.dressedPrice) * this.dollQuantity());
      this.showTotal(cents);
      // The store's accessory discounts count units, including repeated styles.
      this.renderDiscount(units);
    }
    accessoryTotal() {
      return this.products.reduce((sum, row) => {
        if (!row.querySelector('[data-wardrobe-check]').checked) return sum;
        const field = row.querySelector('[data-wardrobe-variant]');
        const option = field.tagName === 'SELECT' ? field.selectedOptions[0] : field;
        return sum + Number(option?.dataset.price || 0) * this.accessoryQuantity(row);
      }, 0);
    }
    showTotal(cents) {
      this.displayTotal = cents;
      this.querySelector('[data-wardrobe-total]').textContent = this.format(cents);
      if (this.checkoutPrice) this.checkoutPrice.textContent = this.format(cents);
      window.PipkindBfcm?.renderSelection(this);
    }
    showConfirmedDiscount(cart) {
      const matching = item => (item.discounts || []).find(discount => /^Accessories Discount (\d+(?:\.\d+)?)%$/.test(discount.title));
      const ownAccessories = cart.items.filter(item => item.properties?._pipkind_wardrobe === this.group && ![this.dataset.dressedId, this.dataset.undressedId].includes(String(item.variant_id)));
      const applied = ownAccessories.map(matching).filter(Boolean);
      if (!applied.length) return;
      // Count discounted accessory units across the cart: existing accessories
      // can also make this selection eligible for the store's automatic offer.
      const units = cart.items.reduce((sum, item) => sum + (matching(item) ? item.quantity : 0), 0);
      const percent = Math.max(...applied.map(discount => Number(discount.title.match(/(\d+(?:\.\d+)?)%/)[1])));
      this.renderDiscount(units);
      const status = this.querySelector('[data-wardrobe-discount-status]');
      if (status) status.textContent = `${percent}% accessory discount applied in your cart.`;
    }
    scheduleSync() {
      this.cartReady = false;
      this.failed = false;
      this.dirty = true;
      clearTimeout(this.timer);
      this.updatePurchaseUI();
      this.dispatchSelection();
      this.timer = setTimeout(() => this.sync(), 180);
    }
    async request(path, options) {
      const response = await fetch(`${this.root}${path}`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          ...(options?.headers || {})
        },
        ...options
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error(data.description || data.message || 'Could not update the cart.');
      return data;
    }
    async restoreFromCart(force = false) {
      const revision = this.revision;
      try {
        const cart = await this.request('cart.js');
        this.bfcmCart = cart;
        window.PipkindBfcm?.renderSelection(this);
        if (this.selectionDirty) return;
        if (!this.isConnected || this.dirty || this.syncing || revision !== this.revision || this.touched && !force) return;
        const owned = cart.items.filter(item => item.properties?._pipkind_wardrobe === this.group);
        const base = owned.find(item => [this.dataset.undressedId, this.dataset.dressedId].includes(String(item.variant_id)));
        if (!base && !force) return;
        this.cartIntent = Boolean(base);
        this.lastConfirmedCart = cart;
        if (base) {
          this.mode = String(base.variant_id) === this.dataset.undressedId ? 'custom' : 'dressed';
          if (this.quantityInput) this.quantityInput.value = String(base.quantity);
        }
        this.products.forEach(row => {
          const field = row.querySelector('[data-wardrobe-variant]');
          const match = owned.find(item => field.tagName === 'SELECT' ? [...field.options].some(option => option.value === String(item.variant_id)) : field.value === String(item.variant_id));
          row.querySelector('[data-wardrobe-check]').checked = Boolean(match);
          row.quantityInput.value = String(match?.quantity || 1);
          if (match && field.tagName === 'SELECT') field.value = String(match.variant_id);
        });
        this.selectionDirty = false;
        this.extraOpen = this.mode === 'custom';
        this.cartReady = Boolean(base);
        this.failed = false;
        this.updateModeUI();
        this.renderPreview();
        if (base) {
          this.showTotal(owned.reduce((sum, item) => sum + (item.final_line_price ?? item.line_price ?? 0), 0));
          this.showConfirmedDiscount(cart);
        }
        this.status.textContent = base ? 'Your selection is in your cart. You can review quantities before checkout.' : '';
        this.switchMedia(this.mode);
        this.dispatchSelection();
      } catch (_) {/* Leave the current selection intact when the cart cannot be read. */}
    }
    requestCartOpen(trigger, direct = false) {
      if (!this.baseAvailable() || this.pendingCartOpen) return;
      this.touched = true;
      this.pendingCheckout = direct;
      this.pendingCartOpen = trigger;
      if (!this.builderActive()) {
        this.cartIntent = true;
        this.updateModeUI();
        this.renderPreview();
      }
      if (this.dirty || this.syncing || !this.cartReady) {
        if (!this.syncing) {
          clearTimeout(this.timer);
          this.dirty = true;
          this.sync();
        }
        return;
      }
      this.finishPurchase();
    }
    finishPurchase() {
      const trigger = this.pendingCartOpen;
      const direct = this.pendingCheckout;
      this.pendingCartOpen = null;
      this.pendingCheckout = false;
      if (direct) {
        const target = window.PipkindBfcm?.discountUrl(`${this.root}checkout`);
        window.location.assign(target || `${this.root}checkout`);
      } else this.openBag(trigger);
    }
    openBag(trigger) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') drawer.open(trigger);else window.location.assign(`${this.root}cart`);
    }
    async sync() {
      if (this.syncing || !this.isConnected) return;
      this.syncing = true;
      this.cartReady = false;
      this.failed = false;
      this.status.classList.remove('is-error');
      this.status.textContent = 'Updating your selection…';
      this.updatePurchaseUI();
      window.dispatchEvent(new CustomEvent('resident-wardrobe:sync', {
        detail: {
          pending: true
        }
      }));
      let confirmedCart;
      try {
        while (this.dirty && this.isConnected) {
          this.dirty = false;
          const desired = this.selectedItems();
          const wanted = new Map(desired.map(item => [String(item.id), item]));
          let cart = await this.request('cart.js');
          // Add a new base before removing the old one, so an unavailable new
          // variant does not remove the shopper's existing doll.
          const currentIds = new Set(cart.items.filter(item => item.properties?._pipkind_wardrobe === this.group).map(item => String(item.variant_id)));
          const missing = desired.filter(item => !currentIds.has(String(item.id)));
          if (missing.length) {
            await this.request('cart/add.js', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                items: missing
              })
            });
            cart = await this.request('cart.js');
          }
          const owned = cart.items.filter(item => item.properties?._pipkind_wardrobe === this.group);
          for (const line of owned) {
            const target = wanted.get(String(line.variant_id))?.quantity || 0;
            const current = cart.items.find(item => item.properties?._pipkind_wardrobe === this.group && String(item.variant_id) === String(line.variant_id));
            if (current && current.quantity !== target) {
              // change.js validates inventory; update.js can bypass inventory checks.
              cart = await this.request('cart/change.js', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  id: current.key,
                  quantity: target
                })
              });
            }
          }
          confirmedCart = await this.request('cart.js');
          const confirmed = confirmedCart.items.filter(item => item.properties?._pipkind_wardrobe === this.group);
          if (confirmed.length !== wanted.size || confirmed.some(item => item.quantity !== wanted.get(String(item.variant_id))?.quantity)) throw new Error('Your selected quantities could not be confirmed. Please adjust the quantities and retry.');
        }
        if (!this.isConnected) return;
        await this.updateCartSections(confirmedCart, ++this.sectionsRevision);
        if (this.dirty) return;
        this.cartReady = true;
        this.lastConfirmedCart = confirmedCart;
        const ownLines = confirmedCart.items.filter(item => item.properties?._pipkind_wardrobe === this.group);
        this.showTotal(ownLines.reduce((sum, item) => sum + (item.final_line_price ?? item.line_price ?? 0), 0));
        this.showConfirmedDiscount(confirmedCart);
        this.status.textContent = 'Your selection is in your cart. You can review quantities before checkout.';
      } catch (error) {
        this.failed = true;
        this.cartReady = false;
        this.pendingCartOpen = null;
        this.pendingCheckout = false;
        this.status.classList.add('is-error');
        this.status.textContent = `${error.message} Adjust your selection or choose Retry update.`;
      } finally {
        this.syncing = false;
        this.updatePurchaseUI();
        window.dispatchEvent(new CustomEvent('resident-wardrobe:sync', {
          detail: {
            pending: false,
            cart: confirmedCart
          }
        }));
        if (this.dirty) this.scheduleSync();else if (this.cartReady && this.pendingCartOpen) this.finishPurchase();
      }
    }
  }
  // The existing discount and cart-section rendering methods are attached below.
  window.PipkindResidentWardrobe = ResidentWardrobe;
}
if (!customElements.get('resident-cart-bar')) {
  class ResidentCartBar extends HTMLElement {
    connectedCallback() {
      this.wardrobe = document.querySelector('resident-wardrobe');
      this.button = this.querySelector('[data-cart-bar-checkout]');
      this.count = this.querySelector('[data-cart-bar-count]');
      this.eyebrow = this.querySelector('[data-cart-bar-eyebrow]');
      this.total = this.querySelector('[data-cart-bar-total]');
      this.abort = new AbortController();
      const listen = (node, event, callback) => node.addEventListener(event, callback, {
        signal: this.abort.signal,
        passive: event === 'scroll'
      });
      listen(this.button, 'click', () => this.wardrobe?.requestCartOpen(this.button));
      this.onChange = () => {
        this.render();
        this.updateVisibility();
      };
      listen(window, 'resident-wardrobe:selection', this.onChange);
      listen(window, 'resident-wardrobe:sync', this.onChange);
      listen(window, 'scroll', () => this.updateVisibility());
      listen(window, 'resize', () => this.updateVisibility());
      this.render();
      this.updateVisibility();
    }
    disconnectedCallback() {
      this.abort?.abort();
    }
    render() {
      const wardrobe = this.wardrobe;
      if (!wardrobe?.config) return;
      const pending = wardrobe.dirty || wardrobe.syncing;
      this.eyebrow.textContent = wardrobe.failed ? 'UPDATE FAILED' : pending ? 'UPDATING SELECTION' : `${wardrobe.cartReady ? 'IN CART' : 'YOUR SELECTION'} · ${wardrobe.mode === 'custom' ? 'DOLL ONLY' : 'DRESSED'}`;
      const units = wardrobe.products.reduce((sum, row) => sum + (row.querySelector('[data-wardrobe-check]').checked ? wardrobe.accessoryQuantity(row) : 0), 0);
      this.count.textContent = `${this.dataset.productTitle} × ${wardrobe.dollQuantity()}${units ? ` + ${units} extras` : ''}`;
      this.total.textContent = `${pending || wardrobe.failed ? 'Est. ' : ''}${wardrobe.format(wardrobe.displayTotal ?? wardrobe.estimatedTotal ?? Number(this.dataset.dressedPrice))}`;
      this.button.textContent = wardrobe.failed ? 'Retry update' : pending ? 'Updating…' : wardrobe.cartReady ? 'View cart' : 'Add to cart';
      this.button.disabled = !wardrobe.baseAvailable();
      this.button.setAttribute('aria-busy', String(pending));
      const image = this.querySelector('[data-cart-bar-images] img');
      if (image) image.src = wardrobe.mode === 'custom' ? wardrobe.dataset.undressedImage : wardrobe.dataset.dressedImage;
    }
    updateVisibility() {
      const primary = this.wardrobe?.checkout;
      const wardrobeRect = this.wardrobe?.getBoundingClientRect();
      const rect = primary?.getBoundingClientRect();
      const visible = rect && wardrobeRect && rect.bottom < 88 && !document.querySelector('cart-drawer.active');
      this.classList.toggle('is-visible', Boolean(visible));
      this.inert = !visible;
    }
  }
  window.PipkindResidentCartBar = ResidentCartBar;
}
if (window.PipkindResidentWardrobe && !customElements.get('resident-wardrobe')) {
  window.PipkindResidentWardrobe.prototype.renderDiscount = function (selectedCount) {
    const panel = this.querySelector('[data-wardrobe-discount]');
    if (!panel) return;
    const tiers = [...(this.config.discountTiers || [])].sort((a, b) => a.count - b.count);
    panel.hidden = !tiers.length;
    if (!tiers.length) return;
    const next = tiers.find(tier => selectedCount < tier.count);
    const earned = [...tiers].reverse().find(tier => selectedCount >= tier.count);
    const status = panel.querySelector('[data-wardrobe-discount-status]');
    if (next && earned) {
      status.textContent = `${earned.percent}% offer threshold reached · add ${next.count - selectedCount} more ${next.count - selectedCount === 1 ? 'accessory' : 'accessories'} for ${next.percent}%`;
    } else if (next) {
      status.textContent = `Add ${next.count - selectedCount} ${next.count - selectedCount === 1 ? 'accessory' : 'accessories'} to reach ${next.percent}% off`;
    } else if (earned) {
      status.textContent = `${earned.percent}% offer threshold reached`;
    }
    const maximum = tiers[tiers.length - 1]?.count || 1;
    panel.querySelector('[data-wardrobe-discount-fill]').style.width = `${Math.min(100, selectedCount / maximum * 100)}%`;
    const track = panel.querySelector('[data-wardrobe-discount-track]');
    track.setAttribute('aria-valuenow', Math.min(selectedCount, maximum));
    track.setAttribute('aria-valuemax', maximum);
  };
  window.PipkindResidentWardrobe.prototype.updateCartSections = async function (cart, revision) {
    let sections = {};
    try {
      // Shopify returns the cart JSON, not rendered sections, when this
      // endpoint receives Accept: application/json.
      sections = await this.request('cart?sections=cart-drawer,cart-icon-bubble', {
        headers: {
          Accept: 'text/html'
        }
      });
    } catch (_) {/* Fall back to the complete cart page below. */}
    if (revision !== this.sectionsRevision) return;
    const parsed = new DOMParser();
    const drawer = document.querySelector('cart-drawer');
    const bubble = document.getElementById('cart-icon-bubble');
    let drawerSource = sections['cart-drawer'] ? parsed.parseFromString(sections['cart-drawer'], 'text/html').querySelector('#CartDrawer') : null;
    let bubbleSource = sections['cart-icon-bubble'] ? parsed.parseFromString(sections['cart-icon-bubble'], 'text/html').querySelector('#shopify-section-cart-icon-bubble, .shopify-section') : null;
    if (drawer && (!drawerSource || drawerSource.querySelectorAll('.cart-item').length !== cart.items.length) || bubble && !bubbleSource) {
      const response = await fetch(`${this.root}cart`, {
        credentials: 'same-origin',
        headers: {
          Accept: 'text/html'
        }
      });
      if (response.ok) {
        const fullPage = parsed.parseFromString(await response.text(), 'text/html');
        if (!drawerSource || drawerSource.querySelectorAll('.cart-item').length !== cart.items.length) {
          drawerSource = fullPage.querySelector('cart-drawer #CartDrawer');
        }
        if (!bubbleSource) bubbleSource = fullPage.querySelector('#cart-icon-bubble');
      }
    }
    if (revision !== this.sectionsRevision) return;
    if (drawer && (!drawerSource || drawerSource.querySelectorAll('.cart-item').length !== cart.items.length)) {
      throw new Error('The cart view could not refresh.');
    }
    if (drawer && drawerSource) {
      const hadFocus = drawer.contains(document.activeElement);
      const scrollTop = drawer.querySelector('.drawer__content')?.scrollTop || 0;
      const target = drawer.querySelector('#CartDrawer');
      if (target) {
        target.innerHTML = drawerSource.innerHTML;
        const content = drawer.querySelector('.drawer__content');
        if (content) content.scrollTop = scrollTop;
        if (hadFocus && drawer.classList.contains('active') && typeof trapFocus === 'function') {
          const inner = drawer.querySelector('.drawer__inner');
          trapFocus(inner, inner);
        }
        drawer.classList.toggle('is-empty', cart.item_count === 0);
        drawer.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
      }
    }
    if (bubble && !bubbleSource) throw new Error('The cart count could not refresh.');
    if (bubble && bubbleSource) {
      bubble.innerHTML = bubbleSource.innerHTML;
    }
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'resident-wardrobe'
      });
    }
  };
  customElements.define('resident-wardrobe', window.PipkindResidentWardrobe);
  delete window.PipkindResidentWardrobe;
}
if (window.PipkindResidentCartBar && !customElements.get('resident-cart-bar')) {
  customElements.define('resident-cart-bar', window.PipkindResidentCartBar);
  delete window.PipkindResidentCartBar;
}
if (!customElements.get('resident-shipping-gap')) {
  class ResidentShippingGap extends HTMLElement {
    connectedCallback() {
      this.threshold = Number(this.dataset.threshold);
      this.root = this.dataset.cartRoot || window.Shopify?.routes?.root || '/';
      if (!this.root.endsWith('/')) this.root += '/';
      this.message = this.querySelector('[data-shipping-message]');
      this.requestRevision = (this.requestRevision || 0) + 1;
      this.onSync = event => {
        if (event.detail?.pending) return;
        if (event.detail?.cart) {
          this.requestRevision += 1;
          this.cart = event.detail.cart;
          this.render();
        }
        this.refresh();
      };
      window.addEventListener('resident-wardrobe:sync', this.onSync);
      this.onSelection = () => this.render();
      window.addEventListener('resident-wardrobe:selection', this.onSelection);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.refresh());
      }
      this.refresh();
    }
    disconnectedCallback() {
      this.requestRevision += 1;
      window.removeEventListener('resident-wardrobe:sync', this.onSync);
      window.removeEventListener('resident-wardrobe:selection', this.onSelection);
      this.unsubscribe?.();
    }
    async refresh() {
      const revision = ++this.requestRevision;
      try {
        const response = await fetch(`${this.root}cart.js`, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {
            Accept: 'application/json'
          }
        });
        if (!response.ok) return;
        const cart = await response.json();
        if (revision !== this.requestRevision) return;
        this.cart = cart;
        this.render();
      } catch (_) {/* Keep the last valid message when the cart is unavailable. */}
    }
    render() {
      if (!this.cart || !this.message || !Number.isFinite(this.threshold) || this.threshold < 0) return;
      const cartTotal = Number(this.cart.total_price);
      if (!Number.isFinite(cartTotal) || cartTotal < 0) return;
      const wardrobe = document.querySelector('resident-wardrobe');
      if (wardrobe && !wardrobe.cartReady) {
        const threshold = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: this.cart.currency || window.Shopify?.currency?.active || 'USD',
          maximumFractionDigits: this.threshold % 100 ? 2 : 0
        }).format(this.threshold / 100);
        this.classList.remove('is-unlocked');
        this.message.textContent = `Free U.S. shipping on orders of ${threshold} or more. Confirmed in cart.`;
        return;
      }
      const gap = Math.max(0, this.threshold - cartTotal);
      const currency = this.cart.currency || window.Shopify?.currency?.active || 'USD';
      const amount = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency,
        maximumFractionDigits: gap % 100 ? 2 : 0
      }).format(gap / 100);
      this.classList.toggle('is-unlocked', gap === 0);
      this.message.textContent = gap ? `You're ${amount} away from free U.S. shipping` : 'Free U.S. shipping unlocked';
    }
  }
  customElements.define('resident-shipping-gap', ResidentShippingGap);
}
